import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import Switch from '../../../common/Switch';
import DirectionsCarFilledIcon from '@mui/icons-material/DirectionsCarFilled';

// ─── Animations ────────────────────────────────────────────────────────────────
const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// ─── Layout ────────────────────────────────────────────────────────────────────
const PageWrapper = styled.div`
  width: 100%;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

// ─── Header ────────────────────────────────────────────────────────────────────
const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.03);
  border: 1px solid #e8ecf1;
  padding: 18px 24px;
  position: relative;
  z-index: 1;
`;

const TitleBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconBadge = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
`;

const TitleText = styled.div``;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 2px;
`;

// ─── Language Toggle ────────────────────────────────────────────────────────────
const LangToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 5px 10px;
  flex-shrink: 0;
`;

const LangLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: ${({ active }) => (active ? '#2563eb' : '#94a3b8')};
  transition: color 0.2s;
  user-select: none;
  letter-spacing: 0.3px;
`;

// ─── Divider ───────────────────────────────────────────────────────────────────
const Divider = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin-bottom: 16px;
`;

// ─── Accordion ─────────────────────────────────────────────────────────────────
const AccordionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
`;

const AccordionItem = styled.div`
  border-bottom: 1px solid #e2e8f0;
  &:last-child { border-bottom: none; }
`;

const AccordionHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  background: ${({ open }) => (open ? '#eff6ff' : '#ffffff')};
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;

  &:hover {
    background: ${({ open }) => (open ? '#eff6ff' : '#f8fafc')};
  }
`;

const AccordionLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

const IndexDot = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${({ open }) => (open ? '#2563eb' : '#e2e8f0')};
  color: ${({ open }) => (open ? '#ffffff' : '#64748b')};
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
`;

const HeadingText = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${({ open }) => (open ? '#2563eb' : '#1e293b')};
  transition: color 0.15s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChevronIcon = styled.span`
  font-size: 11px;
  color: ${({ open }) => (open ? '#2563eb' : '#94a3b8')};
  transition: transform 0.2s, color 0.15s;
  transform: rotate(${({ open }) => (open ? '180deg' : '0deg')});
  flex-shrink: 0;
`;

const AccordionBody = styled.div`
  max-height: ${({ open }) => (open ? '800px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const AccordionContent = styled.div`
  padding: 14px 16px;
  background: #fafafa;
  font-size: 18px;
  color: #374151;
  line-height: 1.75;
  border-top: 1px solid #e2e8f0;

  p { margin: 0 0 8px; }
  p:last-child { margin-bottom: 0; }
  ul, ol { padding-left: 20px; margin: 8px 0; }
  li { margin-bottom: 4px; }
  strong { color: #1e293b; }
`;

// ─── State Views ───────────────────────────────────────────────────────────────
const CenterBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  gap: 10px;
`;

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid #e2e8f0;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: ${spin} 0.75s linear infinite;
`;

const StateText = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 0;
  font-weight: 500;
`;

const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #dc2626;
  font-weight: 500;
`;

const RefreshBar = styled.div`
  text-align: center;
  padding: 8px;
  font-size: 12px;
  color: #2563eb;
  font-weight: 600;
`;

// ─── Component ─────────────────────────────────────────────────────────────────
const Advisoryraview = () => {
  const [advisories, setAdvisories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTetum, setShowTetum] = useState(false);
  const [openIndex, setOpenIndex] = useState(0);
  const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

  const fetchAdvisories = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${backendUrl}/roadaccident/roadaccident-advisories/`);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}. Response: ${errorText.substring(0, 200)}`);
      }
      const data = await response.json();
      setAdvisories(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to fetch advisories:", e);
      setError("Failed to load advisories. Please try again later.");
      setAdvisories([]);
    } finally {
      setIsLoading(false);
    }
  }, [backendUrl]);

  useEffect(() => {
    fetchAdvisories();
  }, [fetchAdvisories]);

  if (isLoading && advisories.length === 0 && !error) {
    return (
      <PageWrapper>
        <CenterBox>
          <Spinner />
          <StateText>Loading advisories...</StateText>
        </CenterBox>
      </PageWrapper>
    );
  }

  if (error && advisories.length === 0) {
    return (
      <PageWrapper>
        <CenterBox>
          <span style={{ fontSize: 32 }}>⚠️</span>
          <StateText style={{ color: '#dc2626' }}>{error}</StateText>
        </CenterBox>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <HeaderRow>
        <TitleBlock>
          <IconBadge><DirectionsCarFilledIcon sx={{ fontSize: 28, color: '#2563eb' }} /></IconBadge>
          <TitleText>
            <Title>
              {showTetum
                ? 'Informasaun No Konsellu Kona-ba Asidente Estrada'
                : 'Information and Safety Advice Regarding Road Accidents'}
            </Title>
          </TitleText>
        </TitleBlock>

        <LangToggleWrapper>
          <LangLabel active={!showTetum}>🇬🇧EN</LangLabel>
          <Switch onChange={() => setShowTetum(!showTetum)} />
          <LangLabel active={showTetum}>🇹🇱TT</LangLabel>
        </LangToggleWrapper>
      </HeaderRow>

      <Divider />

      {error && advisories.length > 0 && (
        <ErrorBanner>⚠️ {error}</ErrorBanner>
      )}

      {advisories.length > 0 ? (
        <AccordionList>
          {advisories.map((advisory, index) => {
            const heading = showTetum
              ? advisory.tetumheading || 'Laiha tipu'
              : advisory.englishheading || 'No heading available';
            const description = showTetum
              ? advisory.tetumdescription || '<p>Laiha deskrisaun.</p>'
              : advisory.englishdescription || '<p>No description available.</p>';
            const isOpen = openIndex === index;

            return (
              <AccordionItem key={advisory.id}>
                <AccordionHeader
                  open={isOpen}
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                >
                  <AccordionLeft>
                    <IndexDot open={isOpen}>{index + 1}</IndexDot>
                    <HeadingText open={isOpen}>{heading}</HeadingText>
                  </AccordionLeft>
                  <ChevronIcon open={isOpen}>▼</ChevronIcon>
                </AccordionHeader>
                <AccordionBody open={isOpen}>
                  <AccordionContent
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                </AccordionBody>
              </AccordionItem>
            );
          })}
        </AccordionList>
      ) : (
        <CenterBox>
          <span style={{ fontSize: 36 }}>🚧</span>
          <StateText>
            {isLoading
              ? ''
              : showTetum
              ? 'Laiha avizu kona-ba asidente estrada.'
              : 'No road safety advisories are currently available.'}
          </StateText>
        </CenterBox>
      )}

      {isLoading && advisories.length > 0 && (
        <RefreshBar>⟳ Refreshing data...</RefreshBar>
      )}
    </PageWrapper>
  );
};

export default Advisoryraview;
